import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import FileUpload from '../../components/ui/FileUpload';
import MultiFileUpload from '../../components/ui/MultiFileUpload';
import {
  Plus,
  Edit2,
  Trash2,
  Music,
  Play,
  ExternalLink,
  X,
  Calendar,
  Disc3,
  DollarSign,
  Gift,
  AlertTriangle,
  ListMusic,
  Star,
} from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, getDocs, where, writeBatch } from 'firebase/firestore';

export default function AdminMusic() {
  const [releases, setReleases] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTypeWarning, setShowTypeWarning] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'single',
    releaseDate: '',
    coverImage: '',
    spotifyUrl: '',
    appleMusicUrl: '',
    youtubeUrl: '',
    soundcloudUrl: '',
    description: '',
    // Pricing (for singles)
    isFree: false,
    price: '',
    albumPrice: '',
    // Audio file (for singles)
    audioUrl: '',
    // Multiple tracks (for albums/EPs/mixtapes)
    tracks: [],
    // Main track setting
    isMainTrack: false,
  });

  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'music'), orderBy('releaseDate', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const musicData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReleases(musicData);
    });

    return unsubscribe;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Check if changing type and tracks exist
    if (name === 'type') {
      const isMultiTrack = value !== 'single';
      const hasMultipleTracks = formData.tracks.length > 1;

      // Show warning if switching to single but has multiple tracks
      if (!isMultiTrack && hasMultipleTracks) {
        setShowTypeWarning(true);
        setPendingTypeChange(value);
        return;
      }

      // Show warning if switching from single to multi-track but has single audio
      if (isMultiTrack && formData.audioUrl && formData.tracks.length === 0) {
        // Auto-convert single audio to first track
        const newTrack = {
          id: `track_${Date.now()}`,
          title: formData.title || 'Track 1',
          audioUrl: formData.audioUrl,
          isFree: formData.isFree,
          price: formData.price,
          order: 1,
        };
        setFormData((prev) => ({
          ...prev,
          type: value,
          tracks: [newTrack],
          audioUrl: '',
        }));
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const confirmTypeChange = () => {
    // User confirmed switching to single - keep only first track
    const firstTrack = formData.tracks[0];
    setFormData((prev) => ({
      ...prev,
      type: pendingTypeChange,
      audioUrl: firstTrack?.audioUrl || '',
      isFree: firstTrack?.isFree || false,
      price: firstTrack?.price || '',
      tracks: [],
    }));
    setShowTypeWarning(false);
    setPendingTypeChange(null);
  };

  const cancelTypeChange = () => {
    setShowTypeWarning(false);
    setPendingTypeChange(null);
  };

  const handleTracksChange = (newTracks) => {
    setFormData((prev) => ({ ...prev, tracks: newTracks }));

    // Show warning if multiple tracks uploaded but type is single
    if (newTracks.length > 1 && formData.type === 'single') {
      setShowTypeWarning(true);
      setPendingTypeChange('album');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const releaseData = {
        ...formData,
        updatedAt: new Date(),
      };

      // If this is being set as main track, clear other main tracks first
      if (formData.isMainTrack) {
        const mainTrackQuery = query(
          collection(db, 'music'),
          where('isMainTrack', '==', true)
        );
        const snapshot = await getDocs(mainTrackQuery);
        const batch = writeBatch(db);
        snapshot.docs.forEach((docRef) => {
          // Don't clear the current one being edited
          if (!editingRelease || docRef.id !== editingRelease.id) {
            batch.update(doc(db, 'music', docRef.id), { isMainTrack: false });
          }
        });
        await batch.commit();
      }

      if (editingRelease) {
        await updateDoc(doc(db, 'music', editingRelease.id), releaseData);
      } else {
        releaseData.createdAt = new Date();
        await addDoc(collection(db, 'music'), releaseData);
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving release:', error);
      alert('Failed to save release');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this release?')) return;

    try {
      await deleteDoc(doc(db, 'music', id));
    } catch (error) {
      console.error('Error deleting release:', error);
      alert('Failed to delete release');
    }
  };

  const handleEdit = (release) => {
    setEditingRelease(release);
    setFormData({
      title: release.title || '',
      type: release.type || 'single',
      releaseDate: release.releaseDate || '',
      coverImage: release.coverImage || '',
      spotifyUrl: release.spotifyUrl || '',
      appleMusicUrl: release.appleMusicUrl || '',
      youtubeUrl: release.youtubeUrl || '',
      soundcloudUrl: release.soundcloudUrl || '',
      description: release.description || '',
      isFree: release.isFree || false,
      price: release.price || '',
      albumPrice: release.albumPrice || '',
      audioUrl: release.audioUrl || '',
      tracks: release.tracks || [],
      isMainTrack: release.isMainTrack || false,
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingRelease(null);
    setShowTypeWarning(false);
    setPendingTypeChange(null);
    setFormData({
      title: '',
      type: 'single',
      releaseDate: '',
      coverImage: '',
      spotifyUrl: '',
      appleMusicUrl: '',
      youtubeUrl: '',
      soundcloudUrl: '',
      description: '',
      isFree: false,
      price: '',
      albumPrice: '',
      audioUrl: '',
      tracks: [],
      isMainTrack: false,
    });
  };

  const isMultiTrackType = formData.type !== 'single';

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Music Releases</h2>
          <p className="text-gray-400">Manage your music catalog</p>
        </div>
        <Button
          onClick={openAddModal}
          className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Release
        </Button>
      </div>

      {/* Releases Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {releases.map((release) => (
          <motion.div
            key={release.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="overflow-hidden hover:border-orange-500/50 transition-all">
              {/* Cover Image */}
              <div className="aspect-square bg-gray-800 relative group">
                {release.coverImage ? (
                  <img
                    src={release.coverImage}
                    alt={release.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Disc3 className="w-20 h-20 text-gray-600" />
                  </div>
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(release)}
                    className="border-white text-white hover:bg-white hover:text-black"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(release.id)}
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {/* Type Badge */}
                <span className="absolute top-2 right-2 px-2 py-1 bg-orange-500 text-white text-xs font-semibold rounded capitalize">
                  {release.type}
                </span>
                {/* Free Badge */}
                {release.isFree && (
                  <span className="absolute top-2 left-2 px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    FREE
                  </span>
                )}
                {/* Main Track Badge */}
                {release.isMainTrack && (
                  <span className="absolute bottom-2 left-2 px-2 py-1 bg-yellow-500 text-black text-xs font-semibold rounded flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    MAIN
                  </span>
                )}
              </div>

              <CardContent className="p-4">
                <h3 className="font-bold text-white truncate">{release.title}</h3>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-gray-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {release.releaseDate || 'No date set'}
                  </p>
                  {!release.isFree && release.price && (
                    <span className="text-sm font-semibold text-green-400 flex items-center">
                      <DollarSign className="w-3 h-3" />
                      {release.price}
                    </span>
                  )}
                </div>

                {/* Streaming Links */}
                <div className="flex gap-2 mt-3">
                  {release.spotifyUrl && (
                    <a
                      href={release.spotifyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-500 hover:text-green-400"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {release.youtubeUrl && (
                    <a
                      href={release.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-500 hover:text-red-400"
                    >
                      <Play className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {releases.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No releases yet</h3>
            <p className="text-gray-400 mb-4">Add your first music release to get started</p>
            <Button onClick={openAddModal} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Release
            </Button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {editingRelease ? 'Edit Release' : 'Add New Release'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Song or album title"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
                  >
                    <option value="single">Single</option>
                    <option value="ep">EP</option>
                    <option value="album">Album</option>
                    <option value="mixtape">Mixtape</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="releaseDate">Release Date</Label>
                  <Input
                    id="releaseDate"
                    name="releaseDate"
                    type="date"
                    value={formData.releaseDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Cover Image</Label>
                  <FileUpload
                    type="image"
                    value={formData.coverImage}
                    onChange={(url) => setFormData({ ...formData, coverImage: url })}
                    folder="music/covers"
                    placeholder="https://example.com/cover.jpg"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-white">Streaming Links</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="spotifyUrl">Spotify URL</Label>
                    <Input
                      id="spotifyUrl"
                      name="spotifyUrl"
                      value={formData.spotifyUrl}
                      onChange={handleChange}
                      placeholder="https://open.spotify.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="appleMusicUrl">Apple Music URL</Label>
                    <Input
                      id="appleMusicUrl"
                      name="appleMusicUrl"
                      value={formData.appleMusicUrl}
                      onChange={handleChange}
                      placeholder="https://music.apple.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youtubeUrl">YouTube URL</Label>
                    <Input
                      id="youtubeUrl"
                      name="youtubeUrl"
                      value={formData.youtubeUrl}
                      onChange={handleChange}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="soundcloudUrl">SoundCloud URL</Label>
                    <Input
                      id="soundcloudUrl"
                      name="soundcloudUrl"
                      value={formData.soundcloudUrl}
                      onChange={handleChange}
                      placeholder="https://soundcloud.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Audio File Section */}
              <div className="space-y-4 border-t border-gray-800 pt-6">
                <h4 className="font-semibold text-white flex items-center gap-2">
                  {isMultiTrackType ? (
                    <ListMusic className="w-4 h-4 text-orange-500" />
                  ) : (
                    <Music className="w-4 h-4 text-orange-500" />
                  )}
                  {isMultiTrackType ? 'Tracks' : 'Audio File'}
                </h4>

                {isMultiTrackType ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400">
                      Upload multiple audio files. Set individual pricing and mark tracks as free.
                    </p>
                    <MultiFileUpload
                      tracks={formData.tracks}
                      onChange={handleTracksChange}
                      folder="music/audio"
                      maxSizeMB={100}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Upload or Link Audio</Label>
                    <FileUpload
                      type="audio"
                      value={formData.audioUrl}
                      onChange={(url) => setFormData({ ...formData, audioUrl: url })}
                      folder="music/audio"
                      placeholder="https://example.com/track.mp3"
                      maxSizeMB={100}
                    />
                    <p className="text-xs text-gray-500">
                      Upload MP3 file (up to 100MB) or paste a direct link.
                    </p>
                  </div>
                )}
              </div>

              {/* Pricing Section */}
              <div className="space-y-4 border-t border-gray-800 pt-6">
                <h4 className="font-semibold text-white flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  Pricing
                </h4>

                {isMultiTrackType ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-400">
                      Individual track pricing is set above in the Tracks section. Set the full album/EP price below.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="albumPrice">Full {formData.type.toUpperCase()} Price ($)</Label>
                      <Input
                        id="albumPrice"
                        name="albumPrice"
                        value={formData.albumPrice}
                        onChange={handleChange}
                        placeholder="9.99"
                      />
                      <p className="text-xs text-gray-500">
                        Price for purchasing the complete {formData.type}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Free Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-white">Free Track</p>
                        <p className="text-sm text-gray-400">Allow users to play this track for free on the website</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, isFree: !formData.isFree })}
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          formData.isFree ? 'bg-green-500' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            formData.isFree ? 'translate-x-6' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Pricing fields - only show if not free */}
                    {!formData.isFree && (
                      <div className="space-y-2">
                        <Label htmlFor="price">Track Price ($)</Label>
                        <Input
                          id="price"
                          name="price"
                          value={formData.price}
                          onChange={handleChange}
                          placeholder="0.99"
                        />
                      </div>
                    )}

                    {/* Main Track Toggle - only for free tracks with audio */}
                    {formData.isFree && formData.audioUrl && (
                      <div className="flex items-center justify-between p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <div>
                          <p className="font-medium text-white flex items-center gap-2">
                            <Star className="w-4 h-4 text-yellow-500" />
                            Set as Main Track
                          </p>
                          <p className="text-sm text-gray-400">This track will auto-play when visitors load the website</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, isMainTrack: !formData.isMainTrack })}
                          className={`w-12 h-6 rounded-full transition-colors relative ${
                            formData.isMainTrack ? 'bg-yellow-500' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                              formData.isMainTrack ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Brief description of the release..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                >
                  {isLoading ? 'Saving...' : editingRelease ? 'Update Release' : 'Add Release'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Type Change Warning Modal */}
      <AnimatePresence>
        {showTypeWarning && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gray-900 rounded-xl max-w-md w-full p-6 border border-yellow-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">
                    {pendingTypeChange === 'single'
                      ? 'Switch to Single Track?'
                      : 'Multiple Files Detected'}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {pendingTypeChange === 'single' ? (
                      <>
                        You have {formData.tracks.length} tracks uploaded. Switching to "Single" will keep only the first track and remove the rest.
                      </>
                    ) : (
                      <>
                        You've uploaded {formData.tracks.length} audio files, but the release type is set to "Single". Would you like to change the type to "Album"?
                      </>
                    )}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelTypeChange}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (pendingTypeChange === 'single') {
                          confirmTypeChange();
                        } else {
                          // Change to album
                          setFormData((prev) => ({ ...prev, type: pendingTypeChange }));
                          setShowTypeWarning(false);
                          setPendingTypeChange(null);
                        }
                      }}
                      className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      {pendingTypeChange === 'single' ? 'Switch to Single' : 'Change to Album'}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
